import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Col,
  Flex,
  List,
  Row,
  Tag,
  theme as antTheme,
  Typography,
} from 'antd';

import AccountPlanContext from '@totem/components/accountPlan/AccountPlanContext';
import PlanCard from '@totem/components/accountPlan/PlanCard';
import {
  SectionHeading,
  shortDate,
  StatBox,
} from '@totem/components/accountPlan/utilities';

const QbrHubCard = () => {
  const { data, openEditor } = useContext(AccountPlanContext);
  const { token } = antTheme.useToken();
  if (!data) {
    return null;
  }
  const { organization, qbr, qbrAgenda } = data.summary;
  const { records, cadence } = data.plan.qbr;
  const openQbr = qbr.open[0];

  const emptyBox = {
    padding: '12px 14px',
    border: `1px dashed ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
    height: '100%',
  };

  return (
    <PlanCard
      variant="manual"
      onEdit={() => openEditor({ section: 'qbr' })}
      title="QBR Hub"
    >
      <Row gutter={[12, 12]}>
        <Col xs={24} lg={14}>
          <div style={emptyBox}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                color: token.colorTextSecondary,
              }}
            >
              OPEN QBR PROJECT · LINKED FROM PORTAL
            </div>
            {openQbr ? (
              <Typography.Title level={5} style={{ margin: '4px 0' }}>
                <Link to={`/dashboard/ticket/${openQbr.id}`}>
                  {openQbr.number}
                </Link>{' '}
                {openQbr.summary}
              </Typography.Title>
            ) : (
              <>
                <Typography.Title
                  level={5}
                  type="secondary"
                  style={{ margin: '4px 0' }}
                >
                  No QBR on record
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {organization.name} has no open QBR project in the Portal.
                  QBRs are scheduled automatically in the Portal; the proposed
                  agenda below is ready when one is.
                </Typography.Text>
              </>
            )}
          </div>
        </Col>
        <Col xs={8} lg={3}>
          <StatBox
            label="Last QBR"
            value={
              qbr.lastCompleted
                ? shortDate(
                    qbr.lastCompleted.closedAt ?? qbr.lastCompleted.createdAt,
                  )
                : '—'
            }
          />
        </Col>
        <Col xs={8} lg={3}>
          <StatBox label="Cadence" value={cadence || 'TBD'} />
        </Col>
        <Col xs={8} lg={4}>
          <StatBox label="Open QBR projects" value={qbr.open.length} />
        </Col>
      </Row>

      <Row gutter={24} style={{ marginTop: 4 }}>
        <Col xs={24} lg={12}>
          <SectionHeading>
            Proposed agenda — auto-built from live data
          </SectionHeading>
          {qbrAgenda.length === 0 ? (
            <Typography.Text type="secondary">
              Nothing flagged yet.
            </Typography.Text>
          ) : (
            qbrAgenda.map((item) => (
              <Flex
                key={item.text}
                gap={8}
                align="flex-start"
                style={{ marginBottom: 6 }}
              >
                <Checkbox defaultChecked />
                <div>
                  <div>{item.text}</div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {item.source}
                  </Typography.Text>
                </div>
              </Flex>
            ))
          )}
        </Col>
        <Col xs={24} lg={12}>
          <SectionHeading>Completed QBRs · from Portal</SectionHeading>
          {qbr.completed.length === 0 ? (
            <div style={emptyBox}>
              <Typography.Text strong>No QBR on record</Typography.Text>
              <div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  No completed QBR project in the Portal for {organization.name}
                  .
                </Typography.Text>
              </div>
            </div>
          ) : (
            <List
              size="small"
              bordered
              dataSource={qbr.completed}
              renderItem={(ticket) => {
                const notes = records.find(
                  (record) => record.ticketId === ticket.id,
                );
                return (
                  <List.Item
                    actions={[
                      <Button
                        key="notes"
                        type="link"
                        size="small"
                        onClick={() =>
                          openEditor({
                            section: 'qbr',
                            itemId: notes?.id,
                            ticketId: ticket.id,
                          })
                        }
                      >
                        {notes ? 'Edit notes' : 'Add notes'}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <>
                          <Link to={`/dashboard/ticket/${ticket.id}`}>
                            {ticket.number}
                          </Link>{' '}
                          {ticket.summary}{' '}
                          <Tag>
                            {shortDate(ticket.closedAt ?? ticket.createdAt)}
                          </Tag>
                        </>
                      }
                      description={notes?.outcomes}
                    />
                  </List.Item>
                );
              }}
            />
          )}
        </Col>
      </Row>
    </PlanCard>
  );
};

export default QbrHubCard;
